import { useCallback, useEffect, useMemo, useState } from "react"

import acc from "accounting"
import { App, Card, Col, Divider, Flex, Row, Space, Spin, Table, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import EmptyComponent from "../../Components/Empty"
import { DashboardFiltersBlock } from "../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import TableComponent from "../../Components/Table"
import Images from "../../Images"
import { getDates } from "../../Utils/utils"
import { columnsCampaign, columnsSource } from "./components/columns"
import GenerateLink from "./components/GenerateLink"
import SourceCampaignBlock from "./components/SourceCampaignBlock"
import { getColumns } from "./components/Table/columns"
import TotalUsersChart from "./components/TotalUsersChart"
import UsersModal from "./components/UsersModal"
import Creators from "./reducer"

import "./styles.less"

const pageSize = 10

const { Title, Text } = Typography

const MarketingPage = ({
  isAdmin,
  fetching,
  result,
  saveResult,
  filters,
  getTotalUsersFetching,
  totalUsersData,
  nav: { theme, isMobile },
  sourceData,
  campaignData,
  test_data,
  ...props
}) => {
  const navigate = useNavigate()
  const { notification } = App.useApp()
  const { t } = useTranslation()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const { count, results } = result

  const period_query = getDates(filters.period)

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  useEffect(() => {
    document.title = "Marketing - Chargeback"
  }, [])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: "100%",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      }
    }),
    [count, pageNumber]
  )

  const getTableColumns = useMemo(() => {
    return getColumns({ onSelectRow: handleSelectRow })
  }, [])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  function handleSelectRow(data, type) {
    if (type === "view_users") {
      navigate(`?open_link=${data?.id}`)
    }
  }

  const handleChangeFilter = (newFilters) => {
    if (Array.isArray(newFilters.period)) {
      props.changeFilters({
        __preset_period: filters?.__preset_period,
        period: { type: "custom", period: newFilters.period }
      })
    } else {
      props.changeFilters(newFilters)
    }
  }

  const updateInfo = () => {
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...period_query,
      test_data
    }
    props.getMarketingLinks({ ...data })
  }

  useEffect(() => {
    updateInfo()
  }, [ordering, pageNumber, filters?.period, test_data])

  useEffect(() => {
    if (filters?.period) {
      props.getTotalUsersData({ ...period_query })
      props.getSourceData({ ...period_query })
      props.getCampaignData({ ...period_query })
    }
  }, [filters?.period])

  useEffect(() => {
    if (saveResult) {
      if (saveResult?.message) {
        notification.success({
          message: saveResult?.message,
          ...notificationConfig
        })
      }
      if (saveResult?.type === "generate_link") updateInfo()

      if (["source_add", "campaign_add"]?.includes(saveResult?.type)) {
        const period_query = getDates(filters.period)
        if (saveResult?.type === "campaign_add") props.getCampaignData({ ...period_query })
        if (saveResult?.type === "source_add") props.getSourceData({ ...period_query })
      }
      props.marketingSaveResultReset()
    }
  }, [saveResult, filters?.period])

  return (
    <div className="marketing-page">
      <Flex align="center" justify="space-between">
        <Flex vertical={true} gap={6}>
          <Title level={3} style={{ textAlign: "left", marginBottom: 0 }}>
            Marketing
          </Title>
          <Text type="secondary">Display and analyze the marketing campaign performance.</Text>
        </Flex>

        <Flex gap={12}>
          <GlobalFilterTestData size={"large"} setPageNumber={setPageNumber} />

          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </Flex>
      </Flex>

      <Divider />

      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Title level={5} style={{ textAlign: "left", fontWeight: 600, marginBottom: 0 }}>
            Total amount of users
          </Title>
          <Title
            style={{
              textAlign: "left",
              fontWeight: 600,
              fontSize: 36,
              marginTop: 8,
              marginBottom: 32
            }}
          >
            {acc.formatNumber(totalUsersData?.total_users, 0, ",", ".")}
          </Title>
          <Card title="" className="card-chart-wrapper alerts-overview">
            {!totalUsersData ? (
              <Spin spinning={getTotalUsersFetching}>
                <Flex vertical justify="start" align="center" style={{ height: 254 }}>
                  <img
                    src={Images?.[theme === "dark" ? "alerts_overview_dark" : "alerts_overview"]}
                    alt=""
                    width={320}
                  />
                  <Text style={{ fontSize: 16 }}>No data available yet..</Text>
                </Flex>
              </Spin>
            ) : (
              <TotalUsersChart totalUsersData={totalUsersData} />
            )}
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={t("Source")}
            className="card-chart-wrapper withTitle"
            styles={{ header: { border: "none" } }}
          >
            <Flex gap={40} style={{ marginBottom: 24 }}>
              <Space direction="vertical">
                <Text style={{ fontSize: 28 }}>{sourceData?.total_count || 0}</Text>
              </Space>
            </Flex>

            <Table
              className={"marketing-chart-table"}
              dataSource={sourceData?.result}
              columns={columnsSource}
              pagination={false}
              rowKey="label"
              tableLayout="fixed"
              showHeader={sourceData?.result?.length}
              locale={{
                emptyText: <EmptyComponent type="no_data_available" style={{ minHeight: 124 }} />
              }}
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={t("Campaign")}
            className="card-chart-wrapper withTitle"
            styles={{ header: { border: "none" } }}
          >
            <Flex gap={40} style={{ marginBottom: 24 }}>
              <Space direction="vertical">
                <Text style={{ fontSize: 28 }}>{campaignData?.total_count || 0}</Text>
              </Space>
            </Flex>

            <Table
              className={"marketing-chart-table"}
              dataSource={campaignData?.result}
              columns={columnsCampaign}
              pagination={false}
              rowKey="label"
              tableLayout="fixed"
              showHeader={campaignData?.result?.length}
              locale={{
                emptyText: <EmptyComponent type="no_data_available" style={{ minHeight: 124 }} />
              }}
            />
          </Card>
        </Col>

        <Col xs={12}>
          <SourceCampaignBlock block_type="source" />
        </Col>
        <Col xs={12}>
          <SourceCampaignBlock block_type="campaign" />
        </Col>
      </Row>

      <Flex align="center" justify="space-between">
        <Flex vertical gap={8} style={{ margin: "32px 0 24px" }}>
          <Text style={{ fontWeight: 600, fontSize: 16 }}>Overview</Text>
          <Text type="secondary">See the number of users come via the link.</Text>
        </Flex>

        <GenerateLink />
      </Flex>

      <TableComponent
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
      />

      <UsersModal />
    </div>
  )
}

const mapStateToProps = (state) => ({
  isAdmin: state.auth.isAdmin,
  nav: state.nav,

  fetching: state.marketing.fetching,
  result: state.marketing.result,
  filters: state.marketing.filters,
  sourceData: state.marketing.sourceData,
  campaignData: state.marketing.campaignData,

  saveFetching: state.marketing.saveFetching,
  saveResult: state.marketing.saveResult,
  totalUsersData: state.marketing.totalUsersData,
  getTotalUsersFetching: state.marketing.getTotalUsersFetching,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getMarketingLinks: (params) => dispatch(Creators.getMarketingLinksRequest(params)),
  getSourceData: (params) => dispatch(Creators.getSourceDataRequest(params)),
  getCampaignData: (params) => dispatch(Creators.getCampaignDataRequest(params)),
  marketingSaveResultReset: () => dispatch(Creators.marketingSetProp("saveResult", null)),
  changeFilters: (filters) => dispatch(Creators.marketingSetProp("filters", filters)),
  getTotalUsersData: (params) => dispatch(Creators.getTotalUsersRequest(params))
})

export default connect(mapStateToProps, mapDispatchToProps)(MarketingPage)
